import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Validators,
  UntypedFormGroup,
  UntypedFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import { IDeclarantTypes } from '../../../../../@interfaces/declarantTypes.interface';
import { ITypes } from '../../../../../@interfaces/types.interface';
import { ProviderService } from '../../../../../@pages/provider/provider.service';
import { TributaryService } from '../../../../../@pages/tributary/tributary.service';
import { DeclarantRateService } from '../../../../services/declarant-rate.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { NgxSpinnerService, NgxSpinnerModule } from 'ngx-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '@shared-services';

@Component({
  selector: 'app-register-declarant-rate',
  templateUrl: './register-declarant-rate.component.html',
  styleUrls: ['./register-declarant-rate.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatButtonModule,
    NgxSpinnerModule,
    TranslatePipe,
  ],
})
export class RegisterDeclarantRateComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private providerService = inject(ProviderService);
  private tributaryService = inject(TributaryService);
  private declarantRateService = inject(DeclarantRateService);
  private translate = inject(TranslateService);
  private spinnerService = inject(NgxSpinnerService);
  private alertService = inject(AlertService);
  private destroyRef = inject(DestroyRef);
  private dialogRef = inject(MatDialogRef<RegisterDeclarantRateComponent>);

  declarantRateForm: UntypedFormGroup = this.fb.group({
    id_declarant: ['', [Validators.required]],
    id_type: ['', [Validators.required]],
    value: ['', [Validators.required, Validators.min(0)]],
    uvt: ['', [Validators.required, Validators.min(0)]],
  });
  declarantTypesList: IDeclarantTypes[] = [];
  typesList: ITypes[] = [];

  ngOnInit(): void {
    this.providerService
      .getAllDeclarantTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IDeclarantTypes[]) => {
        this.declarantTypesList = result || [];
      });
    this.tributaryService
      .getAllTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ITypes[]) => {
        this.typesList = result || [];
      });
  }

  textValid(text: string) {
    return (
      this.declarantRateForm.get(text)?.invalid &&
      this.declarantRateForm.get(text)?.touched
    );
  }

  register() {
    this.declarantRateForm.markAllAsTouched();
    if (this.declarantRateForm.invalid === true) {
      this.openAlert(
        'error',
        'tributary.common.error',
        'tributary.declarantRate.messages.requiredFields',
      );
      return;
    }
    this.spinnerService.show();
    this.saveOnDataBase();
  }

  saveOnDataBase() {
    this.declarantRateService
      .createDeclarantRate({
        id_declarant: Number(this.declarantRateForm.value.id_declarant),
        id_type: Number(this.declarantRateForm.value.id_type),
        value: Number(this.declarantRateForm.value.value),
        uvt: Number(this.declarantRateForm.value.uvt),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.spinnerService.hide();
          if (!result) {
            this.openAlert(
              'error',
              'tributary.common.error',
              'tributary.declarantRate.messages.genericError',
            );
            return;
          }
          this.alertService
            .open({
              title: this.translate.instant('tributary.common.success'),
              message: this.translate.instant(
                'tributary.declarantRate.messages.created',
              ),
              showClose: true,
              type: 'success',
            })
            .subscribe({
              accept: () => this.dialogRef.close(true),
              close: () => this.dialogRef.close(true),
            });
        },
        error: (error) => {
          this.spinnerService.hide();
          const code = error?.graphQLErrors?.[0]?.extensions?.code;
          const isDuplicate = code === 'DECLARANT_RATE_ALREADY_EXISTS';
          this.openAlert(
            isDuplicate ? 'warning' : 'error',
            isDuplicate
              ? 'tributary.declarantRate.titles.notCreated'
              : 'tributary.common.error',
            isDuplicate
              ? 'tributary.declarantRate.messages.duplicate'
              : 'tributary.declarantRate.messages.genericError',
          );
        },
      });
  }

  private openAlert(
    type: 'success' | 'error' | 'warning',
    titleKey: string,
    messageKey: string,
  ): void {
    this.alertService.open({
      title: this.translate.instant(titleKey),
      message: this.translate.instant(messageKey),
      showClose: true,
      type,
    });
  }
}
